// docs/142: clothing dyes in words. A hero has one dye (0: the clothes as drawn, 1..12 the twelve of
// `game/itemDyes.gen.ts`); it colours every dyeable piece they wear at once, unless a piece has been dyed on
// its own (docs/156). Which pieces take it is the decision of docs/129 §8 (`ITEM_DYES`); a piece that keeps
// its own colour says so (§8.3). Pure.
import { DYES, ITEM_DYES, type DyeRamp } from '../../game/itemDyes.gen';
import { ITEM_ART, type Slot } from '../../game/itemArt';
import { artDefOf } from '../../game/baseItemArt';

/** What the dyer charges for one piece (fk_grammar `DYE_PRICE`, docs/156 DP2). */
export const DYE_PRICE = 10;
/** What the dyer charges for everything a hero wears (fk_grammar `OUTFIT_DYE_PRICE`, docs/156 DP2). */
export const OUTFIT_DYE_PRICE = 40;
/** Every choice in order: natural (0) first, then the twelve. */
export const DYE_CHOICES: readonly number[] = [0, ...DYES.map((d) => d.id)];

export const dyeRamp = (dye: number): DyeRamp | undefined => DYES.find((d) => d.id === dye);
/** "Natural" for 0 (the cloth as it was drawn), else the dye's name. */
export const dyeName = (dye: number): string => dyeRamp(dye)?.name ?? 'Natural';

/** How a piece takes a dye: `dyes` (some of its colours do), `keeps` (worn, but keeps its own colour) or
 *  `unseen` (nothing on the body: rings, charms, anything in the pack). Base pieces by the art they wear. */
export type DyeTake = 'dyes' | 'keeps' | 'unseen';
export function dyeTake(def: number): DyeTake {
  const art = artDefOf(def);
  const worn = ITEM_ART[art]?.worn;
  if (!worn) return 'unseen';
  if (worn.slot === 'main_hand') return 'keeps';
  return ITEM_DYES[art] ? 'dyes' : 'keeps';
}

/** A worn piece and whether it takes the dye, for the dresser and the dyer's counter. */
export interface DyedPiece { def: number; name: string; slot: Slot | 'unknown'; takes: boolean }

/** The worn pieces of a set of items (by their catalogue def and name), dyeable first. */
export function dyedPieces(items: Iterable<{ def: number; name: string }>): DyedPiece[] {
  const out: DyedPiece[] = [];
  for (const it of items) {
    const t = dyeTake(it.def);
    if (t === 'unseen') continue;
    out.push({ def: it.def, name: it.name, slot: ITEM_ART[artDefOf(it.def)]?.worn?.slot ?? 'unknown', takes: t === 'dyes' });
  }
  return out.sort((a, b) => Number(b.takes) - Number(a.takes));
}

/** "Red: the cloak and boots take it; the steel helm keeps its colour." in one line, for a hero's worn pieces. */
export function dyeSummary(pieces: readonly DyedPiece[]): string {
  const takes = pieces.filter((p) => p.takes).length;
  const keeps = pieces.length - takes;
  if (!pieces.length) return 'Nothing worn to dye.';
  if (!takes) return 'Nothing they wear takes a dye.';
  return `${takes} piece${takes === 1 ? '' : 's'} take${takes === 1 ? 's' : ''} the dye${keeps ? `; ${keeps} keep${keeps === 1 ? 's' : ''} ${keeps === 1 ? 'its' : 'their'} own colour` : ''}.`;
}
